import CloseIcon from '@mui/icons-material/CloseRounded';
import type { SxProps } from '@mui/material';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import debounce from 'lodash.debounce';
import type { ChangeEventHandler, MouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { SearchIcon } from '../Icons/SearchIcon/SearchIcon';

interface Props {
  sx?: SxProps;
  placeholder?: string;
  query: string;
  onChange: (query: string) => void;
}

export function SearchInput({
  sx,
  query,
  onChange,
  placeholder = 'Search',
}: Props) {
  const [intervalValue, setIntervalValue] = useState(query);
  const debouncedChange = useMemo(() => {
    return debounce(onChange, 200);
  }, [onChange]);
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setIntervalValue(event.target.value);
    debouncedChange(event.target.value);
  };
  const handleClear = () => {
    onChange('');
  };
  const handleClick = (event: MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  };

  useEffect(() => {
    setIntervalValue(query);
  }, [query]);

  return (
    <OutlinedInput
      autoFocus
      type="search"
      size="small"
      startAdornment={
        <InputAdornment position="start">
          <SearchIcon />
        </InputAdornment>
      }
      endAdornment={
        query.length > 0 ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={handleClear}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null
      }
      sx={{ borderRadius: 2, ...sx }}
      placeholder={placeholder}
      fullWidth
      value={intervalValue}
      onChange={handleChange}
      onClick={handleClick}
    />
  );
}
